export function $<T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document): T {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element: ${sel}`);
  return el;
}

/** Writes text only when it changed, to avoid layout churn in per-frame HUD updates. */
export function setText(el: HTMLElement, v: string): void {
  const k = el as HTMLElement & { _v?: string };
  if (k._v !== v) {
    k._v = v;
    el.textContent = v;
  }
}

export function setWidth(el: HTMLElement, ratio: number): void {
  const v = `${(Math.max(0, Math.min(1, ratio)) * 100).toFixed(1)}%`;
  const k = el as HTMLElement & { _w?: string };
  if (k._w !== v) {
    k._w = v;
    el.style.width = v;
  }
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> & { class?: string; data?: Record<string, string> } = {},
  ...children: (Node | string | null | false)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  const { class: cls, data, ...rest } = props;
  if (cls) el.className = cls;
  if (data) for (const [k, v] of Object.entries(data)) el.dataset[k] = v;
  Object.assign(el, rest);
  for (const c of children) if (c !== null && c !== false) el.append(c);
  return el;
}

/** A countdown: "4:05" or "1:05:20". */
export function fmtClock(seconds: number): string {
  const t = Math.max(0, Math.ceil(seconds));
  const [hh, mm, ss] = [Math.floor(t / 3600), Math.floor((t % 3600) / 60), t % 60];
  const two = (n: number) => String(n).padStart(2, '0');
  return hh ? `${hh}:${two(mm)}:${two(ss)}` : `${mm}:${two(ss)}`;
}

/** A length of time in words: "40 วินาที", "3 นาที", "1 ชม. 20 นาที". */
export function fmtDuration(seconds: number): string {
  const t = Math.round(seconds);
  if (t < 60) return `${t} วินาที`;
  const [hh, mm] = [Math.floor(t / 3600), Math.round((t % 3600) / 60)];
  return hh ? `${hh} ชม.${mm ? ` ${mm} นาที` : ''}` : `${mm} นาที`;
}

/** A pixel × (inline SVG, crisp edges) for close buttons: two 2-pixel diagonals on an 8×8 grid. */
export function xIcon(): SVGSVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 8 8');
  svg.setAttribute('aria-hidden', 'true');
  let d = '';
  for (let y = 0; y < 8; y++)
    for (const x of new Set([y, Math.min(7, y + 1), 7 - y, Math.max(0, 6 - y)])) d += `M${x} ${y}h1v1h-1z`;
  const path = document.createElementNS(NS, 'path');
  path.setAttribute('d', d);
  path.setAttribute('fill', 'currentColor');
  svg.append(path);
  return svg;
}

/** The close button every window uses: a square bronze ×, named for screen readers and the tooltip. */
export function closeButton(onClose: () => void, label = 'ปิด (Esc)'): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'x-close', title: label });
  b.setAttribute('aria-label', label);
  b.append(xIcon());
  b.addEventListener('click', onClose);
  return b;
}

/** Turns an existing button into the shared close button (keeps its listeners). */
export function asCloseButton(b: HTMLElement, label = 'ปิด (Esc)'): void {
  b.className = 'x-close';
  b.title = label;
  b.setAttribute('aria-label', label);
  b.replaceChildren(xIcon());
}
